import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle, Save, Edit3, Lock, ShieldCheck, User, Upload } from 'lucide-react';
import CreatorLayout from '../components/CreatorLayout';
import { useAuth } from '../context/AuthContext';
import { getApiUrl, getAssetUrl } from '../config/config';

// ZADRŽANI SVI TVOJI ORIGINALNI STILOVI
const SettingsContainer = styled.div`min-height: 100vh; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); padding: 40px 20px;`;
const SettingsHeader = styled.div`max-width: 1200px; margin: 0 auto; margin-bottom: 40px;`;
const SettingsTitle = styled.h1`font-family: var(--font-heading); font-size: 3rem; font-weight: 700; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 32px;`;
const TabsContainer = styled.div`display: flex; gap: 0; border-bottom: 2px solid #333333; margin-bottom: 40px; overflow-x: auto;`;
const Tab = styled.button`padding: 16px 32px; background: transparent; border: none; font-family: var(--font-heading); font-size: 1.1rem; font-weight: 600; color: ${props => props.$active ? '#e0c16f' : '#cccccc'}; cursor: pointer; position: relative; transition: all 0.3s ease; white-space: nowrap; &:hover { color: #e0c16f; } &::after { content: ''; position: absolute; bottom: -2px; left: 0; right: 0; height: 3px; background: ${props => props.$active ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : 'transparent'}; transition: background 0.3s ease; }`;
const TabContent = styled.div`max-width: 100%; margin: 0 auto;`;
const Section = styled.div`background: #111111; border: 2px solid #333333; border-radius: 16px; padding: 32px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); margin-bottom: 32px;`;
const SectionHeader = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; flex-wrap: wrap; gap: 16px; h3 { font-family: var(--font-heading); font-size: 1.5rem; font-weight: 600; color: #ffffff; margin: 0; } .edit-button { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; border: none; padding: 8px 16px; border-radius: 6px; font-family: var(--font-heading); font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(224, 193, 111, 0.3); } }`;
const FormGroup = styled.div`margin-bottom: 24px;`;
const Label = styled.label`display: flex; align-items: center; gap: 8px; font-family: var(--font-heading); font-size: 1rem; font-weight: 600; color: #ffffff; margin-bottom: 8px;`;
const Input = styled.input`width: 100%; padding: 12px 16px; border: 2px solid #333333; border-radius: 8px; font-family: var(--font-body); font-size: 1rem; background: #000000; color: #ffffff; transition: border-color 0.3s ease; &::placeholder { color: #666666; } &:focus { outline: none; border-color: #e0c16f; background: #111111; }`;
const Button = styled.button`padding: 12px 24px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; border: none; border-radius: 8px; font-family: var(--font-heading); font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px; &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(224, 193, 111, 0.3); } &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const Textarea = styled.textarea`width: 100%; padding: 12px 16px; border: 2px solid #333333; border-radius: 8px; font-family: var(--font-body); font-size: 1rem; background: #000000; color: #ffffff; min-height: 100px; resize: vertical; &:focus { outline: none; border-color: #e0c16f; }`;
const Tag = styled.span`display: flex; align-items: center; gap: 8px; padding: 6px 12px; background: #333333; color: #ffffff; border-radius: 20px; font-family: var(--font-body); font-size: 0.9rem; .remove { cursor: pointer; color: #e0c16f; }`;
const ErrorMessage = styled.div`color: #ff6b6b; font-family: var(--font-body); font-size: 0.9rem; margin-top: 8px; display: flex; align-items: center; gap: 6px;`;
const SuccessMessage = styled.div`color: #51cf66; font-family: var(--font-body); font-size: 0.9rem; margin-top: 8px; display: flex; align-items: center; gap: 6px;`;
const LoadingSpinner = styled.div`display: inline-block; width: 16px; height: 16px; border: 2px solid #e0c16f; border-radius: 50%; border-top-color: transparent; animation: spin 1s linear infinite; @keyframes spin { to { transform: rotate(360deg); } }`;
const AvatarPreview = styled.div`width:96px;height:96px;border-radius:50%;overflow:hidden;border:3px solid #e0c16f;background:#222;display:flex;align-items:center;justify-content:center;color:#e0c16f;flex-shrink:0;img{width:100%;height:100%;object-fit:cover;}`;

const CreatorSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // STATE ZA PROFILE
  const [profileData, setProfileData] = useState({
    displayName: '', username: '', email: '', about: '', website: '', tags: [],
    socialLinks: { twitter: '', twitch: '', youtube: '', instagram: '', discord: '', telegram: '' }
  });

  // STATE ZA PASSWORD CHANGE
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [newTag, setNewTag] = useState('');
  const [openNavItem, setOpenNavItem] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');

  useEffect(() => {
    if (user) loadRealProfile();
  }, [user]);

  const loadRealProfile = async () => {
    try {
      setIsFetching(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load profile');
      const data = await response.json();
      const avatarUrl = getAssetUrl(data.avatar_url) || '';
      setAvatarPreview(avatarUrl);

      setProfileData({
        displayName: data.display_name || data.username || '',
        username: data.username || '',
        email: data.email || '',
        about: data.bio || '',
        website: data.website_url || '',
        tags: typeof data.tags === 'string' ? JSON.parse(data.tags || '[]') : (data.tags || []),
        socialLinks: typeof data.social_links === 'string' ? JSON.parse(data.social_links || '{}') : (data.social_links || { twitter: '', twitch: '', youtube: '', instagram: '', discord: '', telegram: '' })
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/users/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            username: profileData.username, 
            email: profileData.email,
            display_name: profileData.displayName,
            bio: profileData.about,
            website_url: profileData.website,
            social_links: JSON.stringify(profileData.socialLinks),
            tags: JSON.stringify(profileData.tags)
        })
      });
      const profileResult = await response.json();
      if (!response.ok) throw new Error(profileResult.error || 'Failed to update profile');

      if (avatarFile) {
        const avatarData = new FormData();
        avatarData.append('avatar', avatarFile);
        const avatarResponse = await fetch(`${getApiUrl()}/users/profile/avatar`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: avatarData
        });
        const avatarResult = await avatarResponse.json();
        if (!avatarResponse.ok) throw new Error(avatarResult.error || 'Failed to upload avatar');
        setAvatarPreview(getAssetUrl(avatarResult.avatar_url));
        setAvatarFile(null);
      }
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // FUNKCIJA ZA PROMENU ŠIFRE
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setMessage({ type: 'error', text: 'New passwords do not match!' });
    }
    if (passwordData.newPassword.length < 8) {
      return setMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/users/change-password`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to change password');

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && newTag.trim() && profileData.tags.length < 5) {
      setProfileData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  if (isFetching) return <CreatorLayout user={user}><div style={{textAlign:'center', color:'#fff', marginTop:'100px'}}>Loading...</div></CreatorLayout>;

  return (
    <CreatorLayout user={user} activeSection="settings" onNavItemClick={(s) => navigate(`/creator/${s}`)} openNavItem={openNavItem} setOpenNavItem={setOpenNavItem}>
      <SettingsContainer>
        <SettingsHeader>
          <SettingsTitle>Settings</SettingsTitle>
          <TabsContainer>
            <Tab $active={activeTab === 'profile'} onClick={() => {setActiveTab('profile'); setMessage({type:'', text:''});}}>Profile</Tab>
            <Tab $active={activeTab === 'security'} onClick={() => {setActiveTab('security'); setMessage({type:'', text:''});}}>Security</Tab>
            <Tab $active={activeTab === 'payment'} onClick={() => {setActiveTab('payment'); setMessage({type:'', text:''});}}>Payment</Tab>
          </TabsContainer>
        </SettingsHeader>

        {message.text && (
          <div style={{ marginBottom: '20px' }}>
            {message.type === 'success' ? <SuccessMessage><Check size={16} />{message.text}</SuccessMessage> : <ErrorMessage><AlertCircle size={16} />{message.text}</ErrorMessage>}
          </div>
        )}

        <TabContent>
          {activeTab === 'profile' && (
            <Section>
              <SectionHeader>
                <h3>Profile Information</h3>
                {!isEditing ? (
                  <button className="edit-button" onClick={() => setIsEditing(true)}><Edit3 size={16} /> Edit Profile</button>
                ) : (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button onClick={handleProfileUpdate} disabled={isLoading}>{isLoading ? <LoadingSpinner /> : <Save size={16} />} Save</Button>
                    <Button onClick={() => setIsEditing(false)} style={{ background: '#333', color: '#fff' }}>Cancel</Button>
                  </div>
                )}
              </SectionHeader>

              <FormGroup>
                <Label><User size={16}/> Profile Picture</Label>
                <div style={{display:'flex', alignItems:'center', gap:'18px', flexWrap:'wrap'}}>
                  <AvatarPreview>
                    {avatarPreview ? <img src={avatarPreview} alt="Creator avatar" /> : <User size={38} />}
                  </AvatarPreview>
                  {isEditing && (
                    <label style={{display:'flex', alignItems:'center', gap:'8px', color:'#e0c16f', cursor:'pointer'}}>
                      <Upload size={18}/> Choose image
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        style={{display:'none'}}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          setAvatarFile(file);
                          setAvatarPreview(URL.createObjectURL(file));
                        }}
                      />
                    </label>
                  )}
                </div>
                {isEditing && <div style={{color:'#777', marginTop:'8px', fontSize:'.85rem'}}>PNG, JPG, WEBP or GIF, maximum 5MB.</div>}
              </FormGroup>

              <FormGroup>
                <Label>Display Name</Label>
                <Input type="text" value={profileData.displayName} onChange={(e) => setProfileData({...profileData, displayName: e.target.value})} disabled={!isEditing} />
              </FormGroup>

              <FormGroup>
                <Label>About you</Label>
                <Textarea value={profileData.about} onChange={(e) => setProfileData({...profileData, about: e.target.value})} disabled={!isEditing} />
              </FormGroup>

              <FormGroup>
                <Label>Website</Label>
                <Input type="url" placeholder="https://your-site.com" value={profileData.website} onChange={(e) => setProfileData({...profileData, website: e.target.value})} disabled={!isEditing} />
              </FormGroup>

              <FormGroup>
                <Label>What do you do? (Tags)</Label>
                <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                  {profileData.tags.map((tag, i) => <Tag key={i}>{tag} {isEditing && <span className="remove" onClick={() => setProfileData({...profileData, tags: profileData.tags.filter(t => t !== tag)})}>×</span>}</Tag>)}
                  {isEditing && <Input style={{width:'150px', padding:'6px'}} placeholder="Add tag..." value={newTag} onChange={e => setNewTag(e.target.value)} onKeyPress={handleAddTag} />}
                </div>
              </FormGroup>

              <div style={{marginTop:'30px'}}>
                <Label>Social Media Links</Label>
                <div className="social-settings-grid" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'15px'}}>
                   <Input placeholder="Twitter URL" value={profileData.socialLinks.twitter} onChange={e => setProfileData({...profileData, socialLinks: {...profileData.socialLinks, twitter: e.target.value}})} disabled={!isEditing} />
                   <Input placeholder="YouTube URL" value={profileData.socialLinks.youtube} onChange={e => setProfileData({...profileData, socialLinks: {...profileData.socialLinks, youtube: e.target.value}})} disabled={!isEditing} />
                   <Input placeholder="Discord Invite" value={profileData.socialLinks.discord} onChange={e => setProfileData({...profileData, socialLinks: {...profileData.socialLinks, discord: e.target.value}})} disabled={!isEditing} />
                   <Input placeholder="Telegram Channel" value={profileData.socialLinks.telegram} onChange={e => setProfileData({...profileData, socialLinks: {...profileData.socialLinks, telegram: e.target.value}})} disabled={!isEditing} />
                </div>
              </div>
            </Section>
          )}

          {activeTab === 'security' && (
            <Section>
              <SectionHeader>
                <h3>Security & Password</h3>
                <Button onClick={handlePasswordChange} disabled={isLoading}>
                  {isLoading ? <LoadingSpinner /> : <ShieldCheck size={16} />} Update Password
                </Button>
              </SectionHeader>

              <FormGroup>
                <Label><Lock size={16}/> Current Password</Label>
                <Input type="password" placeholder="••••••••" value={passwordData.currentPassword} onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})} />
              </FormGroup>

              <FormGroup>
                <Label><Edit3 size={16}/> New Password</Label>
                <Input type="password" placeholder="At least 8 characters" value={passwordData.newPassword} onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})} />
              </FormGroup>

              <FormGroup>
                <Label><Check size={16}/> Confirm New Password</Label>
                <Input type="password" placeholder="Repeat new password" value={passwordData.confirmPassword} onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})} />
              </FormGroup>
            </Section>
          )}

          {activeTab === 'payment' && (
            <Section>
              <SectionHeader><h3>Payment Settings</h3></SectionHeader>
              <p style={{color:'#888'}}>Stripe integration is active for your account.</p>
            </Section>
          )}
        </TabContent>
      </SettingsContainer>
    </CreatorLayout>
  );
};

export default CreatorSettings;
